import type {
  allPaths,
  GetPaths,
  GetResponseData,
  GetResponseError,
  GetParams,
  MutationMethod,
  MutationResponseData,
  MutationResponseError,
  MutationVariables,
} from '../types/_general.js'
import { type MaybeRef, type MaybeRefOrGetter, type UnwrapRef, type Ref, toValue, computed } from 'vue'
import {
  QueryClient,
  useQuery as _useQuery,
  type UseQueryOptions,
  type UseQueryReturnType,
  useInfiniteQuery as _useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryReturnType,
  useMutation as _useMutation,
  type UseMutationOptions,
  type UseMutationReturnType,
  type QueryFunctionContext,
  type MutationFunctionContext,
  useQueries as _useQueries,
  type QueryObserverResult,
} from '@tanstack/vue-query'
import type { PathsWithMethod, RequiredKeysOf } from 'openapi-typescript-helpers'
import pLimit from 'p-limit'
import { isListResponse, type Item, type ListResponse } from '@/types/_general'
import { apiFetch, createApiError } from '@/utils/api.js'

// Concurrency cap for parallel `getAll` page fetches. HTTP/2 multiplexes
// so this isn't a connection-count concern; the cap exists to avoid
// hammering backend workers with a thundering-herd burst on very large
// result sets.
const GET_ALL_PAGE_CONCURRENCY = 8

type BaseParams = { query?: Record<string, unknown>; path?: Record<string, unknown> } | undefined

export type QueryKey<Method extends 'get', Path extends GetPaths> = readonly [Method, Path, GetParams[Path] | undefined]

export type MutationKey<Method extends MutationMethod, Path extends PathsWithMethod<allPaths, Method>> = readonly [
  Method,
  Path,
  MutationVariables<Method, Path> | undefined,
]

/** Use this instead of ReturnType<typeof useQuery> for component props that accept any query result */
export type UseQueryReturn = UseQueryReturnType<Item, unknown>

declare module '@tanstack/vue-query' {
  interface Register {
    queryKey: readonly ['get', GetPaths, BaseParams]
    mutationKey: readonly [MutationMethod, string]

    mutationMeta: {
      /** @default 'common.ok' */
      successMessage?: string
      /** @default 'system.error' */
      errorMessage?: string
      /** @default true */
      notifyOnSuccess?: boolean
      /** @default true */
      notifyOnError?: boolean
      /**
       * Skip the global mutationCache auto-invalidation. Use when the caller
       * performs bulk mutations and will invalidate manually once at the end
       * (otherwise N successful mutations trigger N full-list re-fetches).
       * @default false
       */
      skipAutoInvalidation?: boolean
    }
    queryMeta: {
      /** @default 'system.error' */
      errorMessage?: string
      /** @default true */
      notifyOnError?: boolean
    }
  }
}

type PageRequestFn = (
  method: string,
  url: string,
  init?: unknown
) => Promise<{ data?: unknown; error?: unknown; response: Response }>

interface RequestParams {
  path?: Record<string, unknown>
  query?: Record<string, unknown>
  header?: Record<string, unknown>
  cookie?: Record<string, unknown>
}

// Fan out remaining pages in parallel with a concurrency cap, then append
// in page-order so the final `_items` array stays sorted according to the
// query's `sort` parameter. `Promise.all` preserves input order regardless
// of completion order. The first page's `data` is mutated in place — its
// `_items` is extended with the remaining pages and `_meta` is replaced
// with the last page's meta so consumers see the final pagination state.
async function fetchRemainingPagesAndMerge(opts: {
  data: ListResponse<Item>
  firstPage: number
  totalPages: number
  method: string
  path: string
  params: RequestParams
  body: unknown
  signal: AbortSignal | undefined
  requestFn: PageRequestFn
}): Promise<void> {
  const { data, firstPage, totalPages, method, path, params, body, signal, requestFn } = opts
  const limit = pLimit(GET_ALL_PAGE_CONCURRENCY)

  const fetchPage = (page: number) =>
    requestFn(method, path, {
      signal,
      params: {
        ...params,
        query: {
          ...(params.query ?? {}),
          page,
        },
      },
      body,
    })

  const pagePromises: Promise<Awaited<ReturnType<PageRequestFn>>>[] = []
  for (let page = firstPage + 1; page <= totalPages; page++) {
    pagePromises.push(limit(() => fetchPage(page)))
  }

  const pageResponses = await Promise.all(pagePromises)
  for (const pageResponse of pageResponses) {
    if (pageResponse.error != null) {
      throw createApiError(pageResponse.error, pageResponse.response.status)
    }
    if (!isListResponse(pageResponse.data)) {
      throw new TypeError('Expected list response when using getAll parameter', { cause: pageResponse.data })
    }
    data._items.push(...pageResponse.data._items)
  }

  const lastResponse = pageResponses.at(-1)
  if (lastResponse != null && isListResponse(lastResponse.data)) {
    data._meta = lastResponse.data._meta
  }
}

export const queryFn = async (
  ...args:
    | [context: QueryFunctionContext]
    | [
        variables: Record<'path' | 'query' | 'header' | 'cookie' | 'body', Record<string, unknown> | undefined>,
        context: MutationFunctionContext,
      ]
) => {
  let context: QueryFunctionContext | MutationFunctionContext
  let _params: Record<'path' | 'query' | 'header' | 'cookie' | 'body', Record<string, unknown> | undefined> & {
    getAll?: boolean
  }
  if (args.length === 1) {
    context = args[0]
    _params = args[0].queryKey[2] as typeof _params
  } else {
    context = args[1]
    _params = args[0]
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [method, path] = 'queryKey' in context ? context.queryKey : context.mutationKey!

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (method == null || path == null)
    throw new TypeError('Missing method or path in queryKey', {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cause: 'queryKey' in context ? context.queryKey : context.mutationKey!,
    })

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const { body, getAll, ...params } = _params ?? {}

  if ('pageParam' in context && context.pageParam != null) {
    params.query ??= {}
    params.query.page = context.pageParam
  }

  try {
    // Cast apiFetch.request to bypass complex type inference with dynamic method/path
    const requestFn = apiFetch.request as (
      method: string,
      url: string,
      init?: unknown
    ) => Promise<{ data?: unknown; error?: unknown; response: Response }>

    const result = await requestFn(method, path, {
      signal: 'signal' in context ? context.signal : undefined,
      params,
      body,
    })

    if (result.error != null) {
      throw createApiError(result.error, result.response.status)
    }

    const data = result.data

    if (getAll && isListResponse(data)) {
      const firstPage = parseInt(data._meta.page ?? '1', 10)
      const maxResults = data._meta.max_results ?? 0
      const total = data._meta.total ?? 0

      if (maxResults > 0 && firstPage * maxResults < total) {
        await fetchRemainingPagesAndMerge({
          data,
          firstPage,
          totalPages: Math.ceil(total / maxResults),
          method,
          path,
          params,
          body,
          signal: 'signal' in context ? context.signal : undefined,
          requestFn,
        })
      }
    }

    return data
  } catch (err: unknown) {
    if (typeof err === 'object' && err !== null && 'status' in err && (err as { status?: number }).status === 419) {
      console.warn(`Session expired for ${path}, request ignored (419)`)
      return null
    }
    throw err
  }
}

export type UseQueryMethod = <Path extends GetPaths, TSelect = GetResponseData[Path]>(
  path: MaybeRefOrGetter<Path>,
  ...[params, options, queryClient]: RequiredKeysOf<GetParams[Path]> extends never
    ? [
        MaybeRefOrGetter<GetParams[Path] & { getAll?: boolean }>?,
        Omit<
          UnwrapRef<
            UseQueryOptions<
              GetResponseData[Path],
              GetResponseError[Path],
              TSelect,
              GetResponseData[Path],
              QueryKey<'get', Path>
            >
          >,
          'queryKey' | 'queryFn'
        >?,
        QueryClient?,
      ]
    : [
        MaybeRefOrGetter<GetParams[Path] & { getAll?: boolean }>,
        Omit<
          UnwrapRef<
            UseQueryOptions<
              GetResponseData[Path],
              GetResponseError[Path],
              TSelect,
              GetResponseData[Path],
              QueryKey<'get', Path>
            >
          >,
          'queryKey' | 'queryFn'
        >?,
        QueryClient?,
      ]
) => UseQueryReturnType<TSelect, GetResponseError[Path]>

export const useQuery: UseQueryMethod = ((
  path: MaybeRefOrGetter<string>,
  params?: MaybeRefOrGetter<unknown>,
  options?: Record<string, unknown>,
  queryClient?: QueryClient
) =>
  _useQuery(
    {
      ...options,
      queryKey: ['get', path, params],
      queryFn,
    } as Parameters<typeof _useQuery>[0],
    queryClient
  )) as UseQueryMethod

interface InfiniteQueryDataWrapper<T> {
  pages: T[]
  pageParams: number[]
}
export type UseInfiniteQueryMethod = <Path extends GetPaths, TSelect = InfiniteQueryDataWrapper<GetResponseData[Path]>>(
  path: MaybeRef<Path>,
  ...[params, options, queryClient]: RequiredKeysOf<GetParams[Path]> extends never
    ? [
        MaybeRef<GetParams[Path]>?,
        Omit<
          UnwrapRef<
            UseInfiniteQueryOptions<InfiniteQueryDataWrapper<GetResponseData[Path]>, GetResponseError[Path], TSelect>
          >,
          'queryKey' | 'queryFn' | 'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam'
        >?,
        QueryClient?,
      ]
    : [
        MaybeRef<GetParams[Path]>,
        Omit<
          UnwrapRef<
            UseInfiniteQueryOptions<InfiniteQueryDataWrapper<GetResponseData[Path]>, GetResponseError[Path], TSelect>
          >,
          'queryKey' | 'queryFn' | 'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam'
        >?,
        QueryClient?,
      ]
) => UseInfiniteQueryReturnType<TSelect, GetResponseError[Path]>

export const useInfiniteQuery: UseInfiniteQueryMethod = ((
  path: MaybeRef<string>,
  params?: MaybeRef<unknown>,
  options?: Record<string, unknown>,
  queryClient?: QueryClient
) =>
  _useInfiniteQuery(
    {
      ...options,
      queryKey: ['get', path, params],
      queryFn,
      initialPageParam: 1,
      getNextPageParam: (lastPage: unknown) => {
        if (!isListResponse(lastPage)) return null

        const maxPage = (lastPage._meta.total ?? 0) / (lastPage._meta.max_results ?? 1)
        if (parseInt(lastPage._meta.page ?? '0', 10) >= maxPage) return null

        return parseInt(lastPage._meta.page ?? '0', 10) + 1
      },
      getPreviousPageParam: (lastPage: unknown) => {
        if (!isListResponse(lastPage)) return null

        if (parseInt(lastPage._meta.page ?? '0', 10) <= 1) return null

        return parseInt(lastPage._meta.page ?? '0', 10) - 1
      },
    } as Parameters<typeof _useInfiniteQuery>[0],
    queryClient
  )) as UseInfiniteQueryMethod

type QueriesResult<Path extends GetPaths> = QueryObserverResult<GetResponseData[Path], GetResponseError[Path]>
export type UseQueriesMethod = <Path extends GetPaths = GetPaths, CombinedResult = QueriesResult<Path>[]>(
  queries: MaybeRef<[Path, GetParams[Path]][]>,
  options?: Omit<Parameters<typeof _useQueries>[0], 'queries' | 'combine'> & {
    combine?: (result: QueriesResult<Path>[]) => CombinedResult
  },
  queryClient?: QueryClient
) => Readonly<Ref<CombinedResult>>

export const useQueries: UseQueriesMethod = ((
  queries: MaybeRef<[string, unknown][]>,
  options?: Omit<Parameters<typeof _useQueries>[0], 'queries'>,
  queryClient?: QueryClient
) =>
  _useQueries(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    {
      queries: computed(() =>
        toValue(queries).map(([path, params]) => ({
          queryKey: ['get', path, params] as const,
          queryFn,
        }))
      ),
      ...options,
    } as Parameters<typeof _useQueries>[0],
    queryClient
  )) as UseQueriesMethod

export type UseMutationMethod = <Method extends MutationMethod, Path extends PathsWithMethod<allPaths, Method>>(
  method: MaybeRef<Method>,
  path: MaybeRef<Path>,
  options?: Omit<
    UnwrapRef<
      UseMutationOptions<
        MutationResponseData<Method, Path>,
        MutationResponseError<Method, Path>,
        MutationVariables<Method, Path>,
        readonly [Method, Path]
      >
    >,
    'mutationFn' | 'mutationKey'
  >,
  queryClient?: QueryClient
) => UseMutationReturnType<
  MutationResponseData<Method, Path>,
  MutationResponseError<Method, Path>,
  MutationVariables<Method, Path>,
  unknown
>

export const useMutation: UseMutationMethod = ((
  method: MaybeRef<MutationMethod>,
  path: MaybeRef<string>,
  options?: Record<string, unknown>,
  queryClient?: QueryClient
) =>
  _useMutation(
    {
      ...options,
      mutationKey: [method, path],
      mutationFn: queryFn,
    } as Parameters<typeof _useMutation>[0],
    queryClient
  )) as UseMutationMethod
